import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowRight, Code, Palette, Smartphone, Monitor, MapPin, Phone, Mail, ExternalLink, Icon, Check } from 'lucide-react';

const Hero: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const floatingIcons = [
    { Icon: Code, delay: 0, x: 100, y: 50 },
    { Icon: Palette, delay: 0.5, x: -80, y: 100 },
    { Icon: Smartphone, delay: 1, x: 120, y: 150 },
    { Icon: Monitor, delay: 1.5, x: -100, y: 200 },
  ];

  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center overflow-hidden bg-slate-950"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-cyan-500/10 rounded-full filter blur-xl animate-pulse delay-1000"></div>
      </div>

      {/* Floating Icons */}
      {floatingIcons.map(({ Icon, delay, x, y }, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ 
            opacity: 0.1, 
            scale: 1,
            x: [0, x * 0.5, x, x * 0.5, 0],
            y: [0, y * 0.5, y, y * 0.5, 0]
          }}
          transition={{ 
            delay: delay + 2,
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute hidden lg:block text-white/20"
          style={{ 
            left: `${20 + (index * 15)}%`, 
            top: `${20 + (index * 10)}%` 
          }}
        >
          <Icon size={48} />
        </motion.div>
      ))}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                <Check></Check> Available for Remote Work
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
            >
              Mario W. Roco
              {/* <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                Roco
              </span> */}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="text-xl md:text-2xl text-gray-300 mb-4 font-medium"
            >
              Full-Stack Developer
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="text-lg text-gray-400 mb-6 max-w-2xl leading-relaxed"
            >
              Detail-oriented professional specializing in Laravel, React, Next.js, PHP, WordPress, express.js and Node.js backend development. 
              Building scalable solutions with modern web technologies and 5+ years of experience.
            </motion.p>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 mb-8 text-sm text-gray-400"
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-indigo-400" />
                Puerto Princesa City, Palawan
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-purple-400" />
                (+63) 936 904 3438
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-pink-400" />
                marioroco2019@gmail.com
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Get In Touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              {/* <Button
                onClick={() => scrollToSection('portfolio')}
                variant="outline"
                size="lg"
                className="border-2 border-white/20 text-white hover:bg-white hover:text-gray-900 font-semibold px-8 py-4 rounded-full transition-all duration-300 backdrop-blur-sm"
              >
                View Portfolio
              </Button>

              <Button
                onClick={() => window.open('https://marioroco.palawanfreelancer.com', '_blank')}
                variant="outline"
                size="lg"
                className="border-2 border-indigo-400/50 text-indigo-300 hover:bg-indigo-400 hover:text-white font-semibold px-6 py-4 rounded-full transition-all duration-300 backdrop-blur-sm"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Website
              </Button> */}
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-white/10"
            >
              {[
                { number: '5+', label: 'Years Experience' },
                // { number: '100+', label: 'Projects Delivered' },
                // { number: '15+', label: 'Technologies' },
              ].map((stat, index) => (
                <div key={index} className="text-center lg:text-left">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.6 + index * 0.1, type: "spring" }}
                    className="text-2xl md:text-3xl font-bold text-blue-400 mb-1"
                  >
                    {stat.number}
                  </motion.div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Visual Element */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Main Card */}
              <motion.div
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative w-80 h-96 bg-slate-800/50 backdrop-blur-lg border border-white/10 rounded-3xl p-8 shadow-2xl"
              >
                <div className="absolute top-4 left-4 flex space-x-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>

                <div className="mt-8 space-y-4">
                  <div className="h-4 bg-white/20 rounded w-3/4"></div>
                  <div className="h-4 bg-white/10 rounded w-1/2"></div>
                  <div className="h-20 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Code className="h-8 w-8 text-orange-400" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-white/10 rounded w-full"></div>
                    <div className="h-3 bg-white/10 rounded w-4/5"></div>
                    <div className="h-3 bg-white/10 rounded w-3/5"></div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Elements */}
              <motion.div
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute -top-4 -right-4 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center shadow-lg"
              >
                <Palette className="h-8 w-8 text-white" />
              </motion.div>

              <motion.div
                animate={{
                  y: [0, -15, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
                className="absolute -bottom-4 -left-4 w-12 h-12 bg-cyan-600 rounded-xl flex items-center justify-center shadow-lg"
              >
                <Monitor className="h-6 w-6 text-white" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-white/60 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;